import React, { useEffect, useContext, useState } from 'react';
import styles from './KanBanView.module.scss';
import DeviceContext from '../context/DeviceContext';
import { KanbanBoard } from '../kanban/KanbanBoard';
import CVLogo from '../assets/CVLogo.svg';
import TomasPP from '../images/Tomas.jpg';
import DavidPP from '../images/David.jpg';
import SelinayPP from '../images/Selinay.jpg';
import { useQuery, gql } from '@apollo/client';
import { GET_PROJECTS_QUERY, GET_TASKS_BY_PROJECT_QUERY } from '../query/gqlQueries.js';
import EditTaskDialog from './EditTaskDialog.js';
import FlowView from './FlowView.js';
import { takeKanbanProject } from './kanbanHandoff';
import UserContext from '../context/UserContext';
import { useFrostTheme } from '../theme/frost';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

const assigneeImagesMap = { David: DavidPP, Tomas: TomasPP, Selinay: SelinayPP };
const assigneeImagesFor = (name) => assigneeImagesMap[name] || null;

export default function KanbanView({ isKanbanViewVisible, setIsKanbanViewVisible }) {
  const username = 'David';
  const { username: me } = useContext(UserContext) || {};
  const [showFlow, setShowFlow] = useState(false);
  const { isMobile } = useContext(DeviceContext);
  // Day or night: the site's one choice (theme/frost.js); the board reads it from the page.
  const [theme, toggleTheme] = useFrostTheme();
  const [projectName, setprojectName] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [hours, setHours] = useState([0, 0, 0, 0, 0]);
  const [projectHours, setProjectHours] = useState({ current: 0, budgeted: 0, available: 0, over: 0, overCount: 0, projectBudget: 0 });
  const taskTypes = ['Admin', '3D', 'Coding', 'Design', 'Learning'];
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Filters & Sorting state
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterDueDate, setFilterDueDate] = useState('');
  const [sortBy, setSortBy] = useState(''); // 'priority' | 'dueDate'

  // Fetch projects
  const { data, loading, error } = useQuery(GET_PROJECTS_QUERY, {
    variables: { username }
  });

  // Fetch tasks for the selected project
  const {
    data: tasksData,
    loading: tasksLoading,
    error: tasksError,
    refetch: refetchTasks
  } = useQuery(GET_TASKS_BY_PROJECT_QUERY, {
    variables: { projectName },
    skip: !projectName
  });

  // Calculate task type hours
  useEffect(() => {
    if (tasksData?.getTasksByProject) {
      const hoursByType = taskTypes.map(type =>
        tasksData.getTasksByProject
          .filter(item => item.task.properties.type === type)
          .reduce((sum, item) => sum + (Number(item.task.properties.timeBudget) || 0), 0)
      );
      setHours(hoursByType);
    } else {
      setHours([0, 0, 0, 0, 0]);
    }
  }, [tasksData]);

  // Calculate project hours
  useEffect(() => {
    if (tasksData?.getTasksByProject && projectName && data?.getProjects) {
      // All from the tasks: what they were given, what's gone into them, and
      // what's left of it to get them done (others' personal tasks aside).
      const items = tasksData.getTasksByProject.filter(item => !(item.task.properties.personal
        && String(item.task.properties.username || '').toLowerCase() !== String(me || '').toLowerCase()));
      const num = (v) => Number(v) || 0;
      const budgeted = items.reduce((sum, item) => sum + num(item.task.properties.timeBudget), 0);
      const current = items.reduce((sum, item) => sum + num(item.task.properties.timeSpent), 0);
      const available = budgeted - current;
      const overTasks = items.filter(item => num(item.task.properties.timeSpent) > num(item.task.properties.timeBudget) && num(item.task.properties.timeBudget) > 0);
      const over = overTasks.reduce((sum, item) => sum + num(item.task.properties.timeSpent) - num(item.task.properties.timeBudget), 0);
      // The client's budget for the whole project, when one's been set.
      const project = data.getProjects.find(p => p.properties.title === projectName);
      const projectBudget = num(project?.properties.budgetedHours);

      setProjectHours({ current, budgeted, available, over, overCount: overTasks.length, projectBudget });
    } else {
      setProjectHours({ current: 0, budgeted: 0, available: 0, over: 0, overCount: 0, projectBudget: 0 });
    }
  }, [tasksData, projectName, data, me]);

  // Handle key presses for desktop
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isMobile && isKanbanViewVisible && event.key === 'e') {
        setIsKanbanViewVisible(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, isKanbanViewVisible]);

  // The project asked for (a project card on Work's home), else the first one.
  useEffect(() => {
    if (!isKanbanViewVisible) return;
    const asked = takeKanbanProject();
    if (asked) { setprojectName(asked); return; }
    if (data?.getProjects?.length > 0 && !projectName) {
      setprojectName(data.getProjects[0].properties.title);
    }
  }, [data, isKanbanViewVisible]);

  const handleClose = () => {
    setIsKanbanViewVisible(false);
  };

  const handleRefresh = () => {
    if (projectName) {
      refetchTasks();
    }
  };

  // Mapping from task status to columnId
  const statusToColumnId = {
    BACKLOG: 'backlog',
    INPROGRESS: 'in-progress',
    VALIDATING: 'validating',
    DONE: 'done'
  };

  // Define column IDs and titles
  const defaultCols = [
    { id: 'backlog', title: 'Backlog' },
    { id: 'in-progress', title: 'In progress' },
    { id: 'validating', title: 'Validating' },
    { id: 'done', title: 'Done' }
  ];

  // Priority ranking
  const priorityRank = {
    urgent: 4,
    high: 3,
    medium: 2,
    med: 2,
    low: 1
  };

  // Filter and sort tasks
  let rawTasks = tasksData?.getTasksByProject || [];

  if (filterAssignee) {
    rawTasks = rawTasks.filter(item => item.task.properties.assignee === filterAssignee);
  }
  if (filterPriority) {
    rawTasks = rawTasks.filter(item => item.task.properties.priority?.toLowerCase() === filterPriority.toLowerCase());
  }
  if (filterDueDate) {
    rawTasks = rawTasks.filter(item => {
      if (!item.task.properties.dueDate) return false;
      const dateStr = new Date(item.task.properties.dueDate).toISOString().split('T')[0];
      return dateStr === filterDueDate;
    });
  }

  if (sortBy === 'priority') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const pA = priorityRank[a.task.properties.priority?.toLowerCase()] || 1;
      const pB = priorityRank[b.task.properties.priority?.toLowerCase()] || 1;
      return pB - pA;
    });
  } else if (sortBy === 'dueDate') {
    rawTasks = [...rawTasks].sort((a, b) => {
      const dA = new Date(a.task.properties.dueDate || '9999-12-31').getTime();
      const dB = new Date(b.task.properties.dueDate || '9999-12-31').getTime();
      return dA - dB;
    });
  }

  // Subtasks show inside their task (a bar of how many are done), not as cards;
  // someone else's personal task isn't shown — as in the Work app.
  const subsByParent = {};
  for (const item of tasksData?.getTasksByProject || []) {
    const parent = item.task.properties.parentId;
    if (!parent) continue;
    const e = subsByParent[parent] || (subsByParent[parent] = { done: 0, total: 0 });
    e.total += 1;
    if (item.status === 'DONE') e.done += 1;
  }
  const hiddenFromMe = (item) => item.task.properties.personal && String(item.task.properties.username || '').toLowerCase() !== String(me || '').toLowerCase();
  rawTasks = rawTasks.filter((item) => !item.task.properties.parentId && !hiddenFromMe(item));

  // Transform tasks for KanbanBoard
  const tasks = rawTasks.map((item) => ({
    subs: subsByParent[item.task.identity] || null,
    avatar: assigneeImagesFor(item.task.properties.assignee),
    id: item.task.identity,
    identity: item.task.identity,   // needed by EditTaskDialog for update/delete
    columnId: statusToColumnId[item.status] || 'backlog',
    status: item.status,
    content: item.task.properties.title,
    assignee: item.task.properties.assignee,
    properties: item.task.properties
  }));

  // Get top 3 priority tasks
  const topPriorityTasks = tasksData?.getTasksByProject
    ?.slice() // Create a copy to avoid mutating original
    .sort((a, b) => {
      const priorityA = priorityRank[a.task.properties.priority?.toLowerCase()] || 1;
      const priorityB = priorityRank[b.task.properties.priority?.toLowerCase()] || 1;
      return priorityB - priorityA; // Descending order
    })
    .slice(0, 3) || [];

  // Assignee to profile picture mapping
  const assigneeImages = assigneeImagesMap;

  // Priority to color class mapping
  const priorityColors = {
    urgent: styles.red,
    high: styles.orange,
    medium: styles.green,
    med: styles.green,
    low: styles.grey
  };

  // ── the panels ──
  const fmtH = (h) => String(Math.round((Number(h) || 0) * 10) / 10);
  const STATUS_LABEL = { BACKLOG: 'Backlog', TODO: 'Backlog', INPROGRESS: 'Doing', VALIDATING: 'Validating', DONE: 'Done' };
  const allItems = (tasksData?.getTasksByProject || []).filter((item) => !item.task.properties.parentId && !hiddenFromMe(item));
  const openTasks = allItems.filter((item) => item.status !== 'DONE' && item.status !== 'CANCELLED');
  // Hours budgeted by kind of work, biggest first.
  const splitMap = {};
  for (const item of allItems) {
    const t = item.task.properties.type || 'Other';
    splitMap[t] = (splitMap[t] || 0) + (Number(item.task.properties.timeBudget) || 0);
  }
  const split = Object.entries(splitMap).filter(([, h]) => h > 0).sort((a, b) => b[1] - a[1]);
  const splitMax = Math.max(1, ...split.map(([, h]) => h));
  // The open tasks that matter most: most urgent, then soonest due.
  const dueMs = (item) => { const d = item.task.properties.dueDate ? new Date(item.task.properties.dueDate).getTime() : NaN; return isNaN(d) ? Infinity : d; };
  const priorityList = [...openTasks].sort((a, b) =>
    (priorityRank[b.task.properties.priority?.toLowerCase()] || 1) - (priorityRank[a.task.properties.priority?.toLowerCase()] || 1) || dueMs(a) - dueMs(b)
  ).slice(0, 5);
  const openTask = (item) => {
    setSelectedTask({ id: item.task.identity, identity: item.task.identity, status: item.status, content: item.task.properties.title, assignee: item.task.properties.assignee, properties: item.task.properties });
    setIsTaskDialogOpen(true);
  };

  return (
    <div className={styles.kanbanFullView}>
      {showFlow && projectName && (
        <FlowView project={projectName} me={me || username} avatars={assigneeImagesMap}
          onOpenTask={(t) => {
            setShowFlow(false);
            setSelectedTask({ id: t.identity, identity: t.identity, status: t.status, content: t.properties.title, assignee: t.properties.assignee, properties: t.properties });
            setIsTaskDialogOpen(true);
          }}
          onClose={() => { setShowFlow(false); refetchTasks && refetchTasks(); }} />
      )}
      {/* The header, as a Mac app's toolbar: the project (tap to choose another),
          Board · Flow, and round buttons for refresh, day/night and close. */}
      <div className={styles.appHeader}>
        <img src={CVLogo} alt="" className={styles.appLogo} />
        <div className={styles.projectPicker}>
          <button className={styles.projectName} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            {projectName || 'Select a project'}<span className={styles.chev} aria-hidden>⌄</span>
          </button>
          {isDropdownOpen && (
            <ul className={styles.projectMenu}>
              {data?.getProjects.map((project) => (
                <li key={project.elementId} className={project.properties.title === projectName ? styles.projectOn : ''}
                  onClick={() => { setprojectName(project.properties.title); setIsDropdownOpen(false); }}>
                  {project.properties.title}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={styles.segmented} role="tablist" aria-label="View">
          <button className={styles.segOn} role="tab" aria-selected>Board</button>
          <button role="tab" aria-selected={false} onClick={() => setShowFlow(true)} disabled={!projectName}>Flow</button>
        </div>
        <div className={styles.headerSpacer} />
        <button className={styles.roundBtn} onClick={handleRefresh} disabled={tasksLoading || !projectName} title="Refresh" aria-label="Refresh tasks"><RefreshIcon /></button>
        <button className={styles.roundBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'} aria-label="Toggle light or dark mode">
          {theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        </button>
        <button className={styles.roundBtn} onClick={handleClose} title="Close" aria-label="Close"><CloseIcon /></button>
      </div>
      <div className={`${styles.mainGrid} ${styles.grid}`}>
        <div className={styles.rightSubGrid}>
          {/* Where the hours go, by kind of work — bars scaled to the biggest. */}
          <section className={styles.panel}>
            <div className={styles.panelTitle}>Task split</div>
            {split.length === 0 ? <div className={styles.panelEmpty}>No hours budgeted yet.</div> : (
              <div className={styles.splitList}>
                {split.map(([type, h]) => (
                  <div key={type} className={styles.splitRow}>
                    <span className={styles.splitLabel}>{type}</span>
                    <span className={styles.splitTrack}><span style={{ width: `${Math.max(4, (h / splitMax) * 100)}%` }} /></span>
                    <span className={styles.splitValue}>{fmtH(h)} h</span>
                  </div>
                ))}
              </div>
            )}
          </section>
          {/* What to do first: the open tasks that matter most. */}
          <section className={styles.panel}>
            <div className={styles.panelTitle}>Priority items{priorityList.length > 0 && <span className={styles.panelCount}>{openTasks.length} open</span>}</div>
            {priorityList.length === 0 ? <div className={styles.panelEmpty}>Nothing open in this project.</div> : (
              <div className={styles.prioList}>
                {priorityList.map((item) => {
                  const p = item.task.properties;
                  const due = p.dueDate ? new Date(p.dueDate) : null;
                  const late = due && !isNaN(due) && due.getTime() < new Date().setHours(0, 0, 0, 0);
                  const left = Math.max(0, (Number(p.timeBudget) || 0) - (Number(p.timeSpent) || 0));
                  const pr = String(p.priority || 'low').toLowerCase();
                  return (
                    <button key={item.task.identity} className={styles.prioRow} onClick={() => openTask(item)}>
                      <span className={`${styles.prioBar} ${styles['prio_' + (pr === 'med' ? 'medium' : pr)]}`} />
                      <span className={styles.prioText}>
                        <span className={styles.prioTitle}>{p.title}</span>
                        <span className={styles.prioMeta}>
                          {STATUS_LABEL[item.status] || 'Backlog'}
                          {due && !isNaN(due) ? <> · <span className={late ? styles.late : ''}>{late ? 'overdue ' : 'due '}{due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span></> : null}
                          {left > 0 ? ` · ${fmtH(left)} h left` : ''}
                        </span>
                      </span>
                      {assigneeImages[p.assignee] ? <img src={assigneeImages[p.assignee]} alt={p.assignee} className={styles.prioAvatar} /> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
          {/* The project's hours. */}
          <section className={styles.panel}>
            <div className={styles.panelTitle}>Hours</div>
            <div className={styles.hoursGrid}>
              <div className={styles.hourTile}><b>{fmtH(projectHours.budgeted)}<small> h</small></b><span>budgeted on tasks</span></div>
              <div className={styles.hourTile}><b>{fmtH(projectHours.current)}<small> h</small></b><span>spent</span></div>
              <div className={`${styles.hourTile} ${projectHours.available < 0 ? styles.hourOver : ''}`}>
                <b>{fmtH(Math.abs(projectHours.available))}<small> h</small></b><span>{projectHours.available < 0 ? 'over budget' : 'available'}</span>
              </div>
              <div className={`${styles.hourTile} ${projectHours.overCount ? styles.hourOver : ''}`}>
                <b>{projectHours.overCount ? <>{fmtH(projectHours.over)}<small> h</small></> : '0'}</b>
                <span>{projectHours.overCount ? `over on ${projectHours.overCount} task${projectHours.overCount === 1 ? '' : 's'}` : 'tasks over budget'}</span>
              </div>
            </div>
            <div className={styles.hoursBar} title="Spent of the hours budgeted">
              <span className={projectHours.available < 0 ? styles.hoursBarOver : ''} style={{ width: `${projectHours.budgeted ? Math.min(100, (projectHours.current / projectHours.budgeted) * 100) : 0}%` }} />
            </div>
            <div className={styles.panelEmpty}>
              {projectHours.budgeted ? `${fmtH(projectHours.current)} of ${fmtH(projectHours.budgeted)} h used` : 'No hours budgeted on the tasks yet.'}
              {projectHours.projectBudget ? ` · project budget ${fmtH(projectHours.projectBudget)} h${projectHours.projectBudget > projectHours.budgeted ? `, ${fmtH(projectHours.projectBudget - projectHours.budgeted)} h not given to tasks yet` : ''}` : ''}
            </div>
          </section>
          <div className={styles.kanBanContainer}>
            {/* Filters, as a Mac toolbar: pill menus in one frosted bar. */}
            <div className={styles.toolbar}>
              <label className={`${styles.pill} ${filterAssignee ? styles.pillOn : ''}`}>
                <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} aria-label="Assignee">
                  <option value="">Everyone</option>
                  <option value="David">David</option>
                  <option value="Selinay">Selinay</option>
                  <option value="Tomas">Tomas</option>
                </select>
              </label>
              <label className={`${styles.pill} ${filterPriority ? styles.pillOn : ''}`}>
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} aria-label="Priority">
                  <option value="">Any priority</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </label>
              <label className={`${styles.pill} ${filterDueDate ? styles.pillOn : ''}`} title="Due on">
                <span className={styles.pillLabel}>Due</span>
                {/* Empty, Safari would show today's date as if it were chosen — so say "Any date" over it. */}
                {!filterDueDate && <span>Any date</span>}
                <input type="date" className={filterDueDate ? '' : styles.dateEmpty} value={filterDueDate} onChange={e => setFilterDueDate(e.target.value)} aria-label="Due on"
                  onClick={e => { try { e.currentTarget.showPicker(); } catch { /* not supported */ } }} />
              </label>
              <label className={`${styles.pill} ${sortBy ? styles.pillOn : ''}`}>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort">
                  <option value="">Board order</option>
                  <option value="priority">By priority</option>
                  <option value="dueDate">By due date</option>
                </select>
              </label>
              {(filterAssignee || filterPriority || filterDueDate || sortBy) && (
                <button className={styles.clearLink} onClick={() => { setFilterAssignee(''); setFilterPriority(''); setFilterDueDate(''); setSortBy(''); }}>Clear</button>
              )}
            </div>

            {loading || tasksLoading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>Error loading projects: {error.message}</p>
            ) : tasksError ? (
              <p>Error loading tasks: {tasksError.message}</p>
            ) : tasks.length === 0 ? (
              <p>No tasks found for this project.</p>
            ) : (
              <KanbanBoard 
                defaultCols={defaultCols} 
                initialTasks={tasks} 
                refetchTasks={refetchTasks}
                onEditTask={(task) => {
                  setSelectedTask(task);
                  setIsTaskDialogOpen(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
      
      <EditTaskDialog 
        isOpen={isTaskDialogOpen} 
        onClose={() => {
          setIsTaskDialogOpen(false);
          setSelectedTask(null);
          refetchTasks();
        }}
        task={selectedTask}
      />
    </div>
  );
}